import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Search, Loader2, Pill, ShieldAlert, ScrollText, TrendingUp,
  AlertOctagon, Skull, Activity, BarChart2,
} from "lucide-react";

function DrugScoreBadge({ score, matched }) {
  const s = Math.max(0, Math.min(100, Math.round(score || 0)));
  const tier =
    s >= 70 ? { bg: "bg-emerald-500/15", border: "border-emerald-400/40", text: "text-emerald-300" }
    : s >= 40 ? { bg: "bg-amber-500/15",  border: "border-amber-400/40",  text: "text-amber-300" }
    :          { bg: "bg-slate-50",        border: "border-slate-200",     text: "text-slate-500" };
  return (
    <div className="flex flex-col items-start gap-1">
      <span
        data-testid="drug-match-badge"
        className={`inline-flex items-center gap-1 border ${tier.border} ${tier.bg} px-2 py-0.5 font-mono text-xs font-semibold ${tier.text}`}
        title={`Lexical match ${s}/100`}
      >
        <TrendingUp className="h-3 w-3" strokeWidth={2.5} /> {s}
      </span>
      {matched && matched.length > 0 && (
        <div className="flex flex-wrap gap-1 max-w-[140px]">
          {matched.slice(0, 2).map((t) => (
            <span key={t} className="border border-slate-200 px-1 text-[9px] uppercase tracking-wider text-slate-400">
              {t}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DrugResearchPanel() {
  const [q, setQ] = useState("");
  const [mode, setMode] = useState("rld");     // 'rld' | 'faers' | 'label' | 'intel'
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [intel, setIntel] = useState(null);
  const [intelApp, setIntelApp] = useState("ANDA");

  const runSearch = async () => {
    if (mode === "intel") {
      return runIntel();
    }
    if (!q.trim()) return;
    setLoading(true); setRows([]); setTotal(0);
    try {
      const { data } = await api.get(`/drug/${mode}/search`, { params: { q, limit: 25 } });
      setRows(data.results || []);
      setTotal(data.total || 0);
      if ((data.results || []).length === 0) toast.info("No results — try a brand name, INN, or application number");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally { setLoading(false); }
  };

  const runIntel = async () => {
    setLoading(true); setIntel(null);
    try {
      const { data } = await api.get(`/drug/product-code/intel`, { params: { app_type: intelApp, years: 6 } });
      setIntel(data);
      if (!data.total_approvals) toast.info("No approvals in the selected window.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Intel fetch failed");
    } finally { setLoading(false); }
  };

  const tabs = [
    { code: "rld",   label: "Reference Listed Drugs", icon: Pill,       hint: "openFDA drugsfda" },
    { code: "faers", label: "FAERS Adverse Events",   icon: ShieldAlert, hint: "openFDA drug/event" },
    { code: "label", label: "Prescribing Info",       icon: ScrollText,  hint: "openFDA drug/label" },
    { code: "intel", label: "Product-Code Intel",     icon: BarChart2,  hint: "clearances per year" },
  ];

  return (
    <div data-testid="drug-research-root">
      <div className="mb-6">
        <div className="overline mb-2 text-purple-300">openFDA CDER · Live drug data</div>
        <h1 className="font-serif text-3xl font-semibold tracking-tight lg:text-4xl">Drug research.</h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-500">
          Search Reference Listed Drugs (the "predicate" for generics), FAERS adverse events, and
          full FDA Prescribing Information — live from openFDA. Match scoring, sorting, and
          drill-through built in.
        </p>
      </div>

      {/* Mode tabs */}
      <div className="mb-4 flex flex-wrap gap-2">
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = mode === t.code;
          return (
            <button
              key={t.code}
              data-testid={`drug-mode-${t.code}`}
              onClick={() => { setMode(t.code); setRows([]); setTotal(0); setIntel(null); }}
              className={`inline-flex items-center gap-2 border px-3 py-2 text-xs font-medium transition-all ${
                active
                  ? "border-purple-400 bg-purple-500/10 text-purple-100"
                  : "border-slate-200 text-slate-600 hover:border-slate-300 hover:text-slate-900"
              }`}
            >
              <Icon className="h-3.5 w-3.5" /> {t.label}
              <span className="text-slate-400">· {t.hint}</span>
            </button>
          );
        })}
      </div>

      {/* Search bar */}
      {mode !== "intel" ? (
      <div className="mb-6 flex flex-wrap gap-3">
        <div className="flex flex-1 items-center border border-slate-200 bg-white pl-4">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            data-testid="drug-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runSearch()}
            placeholder={
              mode === "rld"   ? "brand, INN, sponsor, or ANDA/NDA/BLA number (e.g. aspirin, ANDA206392)"
              : mode === "faers" ? "brand, INN or active ingredient (e.g. insulin, sertraline)"
              :                    "brand or INN (e.g. ibuprofen, warfarin)"
            }
            className="flex-1 border-none bg-transparent px-3 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
          />
        </div>
        <button
          data-testid="drug-search-btn"
          onClick={runSearch}
          disabled={loading}
          className="inline-flex items-center gap-2 bg-purple-600 px-5 py-3 text-sm font-semibold text-white transition-all hover:bg-purple-500 disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Search
        </button>
      </div>
      ) : (
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="overline text-slate-500">APPLICATION TYPE</div>
        {["ANDA", "NDA", "BLA"].map((t) => (
          <button
            key={t}
            data-testid={`drug-intel-type-${t.toLowerCase()}`}
            onClick={() => setIntelApp(t)}
            className={`border px-3 py-1.5 text-xs font-medium transition-all ${
              intelApp === t
                ? "border-purple-400 bg-purple-500/15 text-purple-100"
                : "border-slate-200 text-slate-600 hover:border-slate-300 hover:text-slate-900"
            }`}
          >
            {t}
          </button>
        ))}
        <button
          data-testid="drug-intel-run"
          onClick={runIntel}
          disabled={loading}
          className="inline-flex items-center gap-2 bg-purple-600 px-5 py-2 text-sm font-semibold text-white transition-all hover:bg-purple-500 disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <BarChart2 className="h-4 w-4" />}
          Load intel
        </button>
        <div className="ml-auto text-[11px] text-slate-400">openFDA drugsfda · last ~6 years</div>
      </div>
      )}

      {mode === "intel" && intel && (
        <div data-testid="drug-intel-panel" className="border border-slate-200 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
            <div>
              <div className="overline text-purple-300">
                {intel.app_type} APPROVALS · LAST {intel.years} YEARS
              </div>
              <div className="mt-1 font-serif text-2xl font-semibold text-slate-900">
                {intel.total_approvals.toLocaleString()} <span className="text-slate-500">total approvals</span>
              </div>
            </div>
          </div>
          <div className="p-6">
            {intel.by_year.length === 0 ? (
              <div className="text-sm text-slate-500">No approvals in window.</div>
            ) : (
              <div className="space-y-2">
                {(() => {
                  const max = Math.max(...intel.by_year.map((r) => r.approvals || 0)) || 1;
                  return intel.by_year.map((r) => (
                    <div key={r.year} className="flex items-center gap-3">
                      <div className="w-16 font-mono text-sm text-slate-600">{r.year}</div>
                      <div className="relative h-6 flex-1 bg-slate-50">
                        <div
                          className="absolute inset-y-0 left-0 bg-purple-500/70"
                          style={{ width: `${(r.approvals / max) * 100}%` }}
                        />
                        <div className="absolute inset-0 flex items-center px-2 text-xs font-semibold text-slate-900">
                          {r.approvals}
                        </div>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}
            <div className="mt-4 border-t border-slate-100 pt-3 text-[11px] text-slate-500">
              {intel.note}
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      {mode !== "intel" && rows.length > 0 && (
        <div className="border border-slate-200">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-3">
            <div className="overline text-slate-500">
              {rows.length} SHOWN · {total.toLocaleString()} TOTAL MATCHES
            </div>
          </div>

          {mode === "rld" && (
            <div className="max-h-[500px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50">
                  <tr className="border-b border-slate-200">
                    <th className="overline w-24 px-3 py-3 text-left">Match</th>
                    <th className="overline px-3 py-3 text-left">Application</th>
                    <th className="overline px-3 py-3 text-left">Brand / Generic</th>
                    <th className="overline px-3 py-3 text-left">Sponsor</th>
                    <th className="overline px-3 py-3 text-left">Dosage / Route</th>
                    <th className="overline px-3 py-3 text-left">TE Code</th>
                    <th className="overline px-3 py-3 text-left">RLD</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.application_number} data-testid="drug-result-row" className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-3"><DrugScoreBadge score={r.match_score} matched={r.matched_terms} /></td>
                      <td className="px-3 py-3 font-mono text-xs text-purple-200">{r.application_number}</td>
                      <td className="max-w-xs px-3 py-3">
                        <div className="text-slate-800">{(r.brand_name || []).join(", ") || "–"}</div>
                        <div className="text-[11px] text-slate-500">{r.generic_name || ""}</div>
                      </td>
                      <td className="px-3 py-3 text-slate-600">{r.sponsor_name}</td>
                      <td className="px-3 py-3 text-slate-600">
                        <div>{r.dosage_form || "–"}</div>
                        <div className="text-[11px] text-slate-500">{(r.route || []).join(", ") || ""}</div>
                      </td>
                      <td className="px-3 py-3 font-mono text-xs text-slate-600">{r.te_code || "–"}</td>
                      <td className="px-3 py-3">
                        {r.reference_drug === "Yes" ? (
                          <span className="inline-flex items-center gap-1 border border-emerald-400/50 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-300">
                            RLD
                          </span>
                        ) : (
                          <span className="text-[11px] text-slate-400">–</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {mode === "faers" && (
            <div className="max-h-[500px] overflow-y-auto">
              <ul className="divide-y divide-slate-200">
                {rows.map((e) => (
                  <li key={e.safetyreportid} data-testid="drug-faers-row" className="px-4 py-3 hover:bg-slate-50">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-mono text-xs text-purple-200">#{e.safetyreportid}</span>
                          {e.serious === "1" && (
                            <span className="inline-flex items-center gap-1 border border-rose-400/50 bg-rose-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-rose-300">
                              <AlertOctagon className="h-2.5 w-2.5" /> SERIOUS
                            </span>
                          )}
                          {e.seriousness_death === "1" && (
                            <span className="inline-flex items-center gap-1 border border-rose-500/60 bg-rose-500/20 px-1.5 py-0.5 text-[10px] font-bold text-rose-200">
                              <Skull className="h-2.5 w-2.5" /> DEATH
                            </span>
                          )}
                          {e.seriousness_hospitalization === "1" && (
                            <span className="border border-amber-400/50 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-amber-300">
                              HOSPITALIZATION
                            </span>
                          )}
                          <span className="text-[11px] text-slate-500">received {e.receivedate || "?"}</span>
                        </div>
                        {e.reactions?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {e.reactions.slice(0, 5).map((rx) => (
                              <span key={rx} className="border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[11px] text-slate-700">
                                {rx}
                              </span>
                            ))}
                          </div>
                        )}
                        {e.drugs?.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-slate-500">
                            <Activity className="h-3 w-3" />
                            {e.drugs.slice(0, 3).map((d, i) => (
                              <span key={i}>{d}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="text-right text-[11px] text-slate-500">
                        {e.patient_sex && <div>{e.patient_sex}</div>}
                        {e.patient_age && <div>age {e.patient_age}</div>}
                        {e.reporter_country && <div>{e.reporter_country}</div>}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {mode === "label" && (
            <div className="max-h-[600px] overflow-y-auto">
              {rows.map((l) => (
                <div key={l.id} data-testid="drug-label-row" className="border-b border-slate-100 p-5 hover:bg-slate-50">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="font-serif text-lg font-semibold text-slate-900">
                        {(l.brand_name || []).join(", ") || "(no brand name)"}
                      </div>
                      <div className="mt-0.5 text-sm text-slate-500">
                        {(l.generic_name || []).join(", ")}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-slate-400">
                        <span>{(l.manufacturer || []).join(", ")}</span>
                        {l.effective_time && <span>· effective {l.effective_time}</span>}
                        {(l.route || []).length > 0 && <span>· {(l.route || []).join(", ")}</span>}
                      </div>
                    </div>
                    {(l.product_type || []).length > 0 && (
                      <span className="border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] uppercase tracking-wider text-slate-600">
                        {(l.product_type || []).join("/")}
                      </span>
                    )}
                  </div>

                  {l.boxed_warning && (
                    <div className="mt-3 border-l-2 border-rose-400 bg-rose-500/5 pl-3 py-2">
                      <div className="overline text-rose-300">BOXED WARNING</div>
                      <div className="mt-1 text-xs text-rose-100/90">{l.boxed_warning}</div>
                    </div>
                  )}
                  {l.indications_and_usage && (
                    <div className="mt-3">
                      <div className="overline text-slate-400">INDICATIONS AND USAGE</div>
                      <div className="mt-1 text-xs text-white/75">{l.indications_and_usage}</div>
                    </div>
                  )}
                  {l.warnings && (
                    <div className="mt-2">
                      <div className="overline text-slate-400">WARNINGS</div>
                      <div className="mt-1 text-xs text-slate-600">{l.warnings}</div>
                    </div>
                  )}
                  {l.adverse_reactions && (
                    <div className="mt-2">
                      <div className="overline text-slate-400">ADVERSE REACTIONS</div>
                      <div className="mt-1 text-xs text-slate-600">{l.adverse_reactions}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
